// ─────────────────────────────────────────────────────────────────────────────
// Artist CNTRL, React task-drawer data layer.
//
// Ports the persistence contract of the LEGACY drawer (site/js/tasks-drawer.js)
// for BOTH task kinds, keeping every write band-scoped (Bible 06.4 tenancy):
//
//   • Standalone tasks (id like "st_…") live in the band's `standalone_tasks`
//     blob. Read: GET /api/data/standalone_tasks. Write: read-modify-write the
//     whole array via PUT /api/data/standalone_tasks { value: [...] }.
//   • Release-checklist tasks (composite id "releaseId:idx") live inside the
//     matching release's `checklist[]` in the band's `releases` blob. Read: GET
//     /api/data/releases → releases[n].checklist[idx]. Write: mutate that item
//     and PUT /api/data/releases { value: [...] }.
//
// SCOPE: an explicit `artistId` (the roster surface's selected artist) is sent as
// X-Band-Id so the read/write lands on THAT assigned artist's band. When omitted
// (the artist's own surface, or the release page) we fall back to the ambient
// band context via authHeaders(), exactly like the legacy drawer's AUTH +
// X-Band-Id header split. We NEVER widen reach: the id passed in is the only band
// touched.
//
// These are the SAME endpoints the legacy drawer + TaskCreateModal call, no new
// worker endpoints are introduced. Release-checklist writes go through the release
// blob (not the legacy /api/tasks/release-checklist worker route), so the whole
// drawer stays a pure client read-modify-write against the band's user_data.
// ─────────────────────────────────────────────────────────────────────────────

import { AUTH_WORKER } from './api';
import { getToken, getCurrentBandId } from './auth';

// ── Shared record shapes (mirror TaskCreateModal / RosterStandaloneTask) ──────

export interface TaskPerson {
  readonly id?: string;
  readonly contactId?: string | number | null;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly name?: string;
  readonly initials?: string;
  readonly email?: string;
  readonly avatarUrl?: string | null;
  readonly avatar_url?: string | null;
  readonly photo_url?: string | null;
  readonly role?: string;
  readonly source?: string;
}

export interface TaskSubtask {
  readonly id: string;
  readonly title: string;
  readonly done: boolean;
}

export interface TaskLink {
  readonly id: string;
  readonly url: string;
  readonly label?: string;
  readonly ts?: string;
}

export interface TaskComment {
  readonly id: string;
  readonly ts: string;
  readonly actor_id?: string;
  readonly actor_name?: string;
  readonly body: string;
  // Optimistic-state flags (client-only, never persisted).
  readonly _pending?: boolean;
  readonly _failed?: boolean;
  readonly _error?: string;
}

export interface TaskActivity {
  readonly ts: string;
  readonly actor_id?: string;
  readonly actor_name?: string;
  readonly action: string;
  readonly from?: unknown;
  readonly to?: unknown;
}

export interface TaskVisibility {
  readonly label: boolean;
  readonly management: boolean;
  readonly booking: boolean;
}

export interface TaskApproval {
  readonly status: string;
  readonly approver_id: string;
  readonly decided_at: string;
  readonly revision_notes: string;
}

/** The hydrated task the drawer renders. Both kinds normalize to this shape
 *  (mirrors fetchStandaloneTaskData()'s unified payload in tasks-drawer.js). */
export interface DrawerTask {
  readonly id: string;
  readonly artistId: string;
  readonly standalone: boolean;
  // Release-checklist coordinates (standalone → releaseId '' / taskIdx -1).
  readonly releaseId: string;
  readonly taskIdx: number;
  readonly releaseTitle: string;
  readonly releaseDate: string;
  title: string;
  due: string;
  status: string;
  done: boolean;
  notes: string;
  assignee: TaskPerson | null;
  assignees: TaskPerson[];
  initiator: TaskPerson | null;
  approval: TaskApproval;
  links: TaskLink[];
  comments: TaskComment[];
  activity: TaskActivity[];
  completed_at: string;
  completed_by: TaskPerson | null;
  archived_forever: boolean;
  visibility: TaskVisibility;
  subtasks: TaskSubtask[];
  contacts: TaskPerson[];
}

// The subset of statuses the standalone RMW whitelist accepts (legacy line 1639).
export const STANDALONE_STATUS_WHITELIST = new Set([
  'open', 'in_progress', 'blocked', 'needs_review', 'approved', 'revisions_requested', 'done',
]);

// ── Headers ───────────────────────────────────────────────────────────────────
// artistId present → explicit X-Band-Id (roster). Absent → ambient band via
// getCurrentBandId (self-band / release page), matching authHeaders().
function taskHeaders(artistId: string | undefined, withBody: boolean): Record<string, string> {
  const h: Record<string, string> = { Authorization: 'Bearer ' + getToken() };
  const band = artistId || getCurrentBandId();
  if (band) h['X-Band-Id'] = band;
  if (withBody) h['Content-Type'] = 'application/json';
  return h;
}

function bust(path: string): string {
  return path + (path.indexOf('?') >= 0 ? '&' : '?') + '_=' + Date.now();
}

async function getBlob(path: string, artistId: string | undefined): Promise<unknown> {
  try {
    const res = await fetch(AUTH_WORKER + path, {
      method: 'GET', headers: taskHeaders(artistId, false), cache: 'no-store',
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

async function putBlob(path: string, artistId: string | undefined, value: unknown): Promise<boolean> {
  try {
    const res = await fetch(AUTH_WORKER + path, {
      method: 'PUT', headers: taskHeaders(artistId, true), body: JSON.stringify({ value }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// Unwrap a user_data blob → its array value ({success,data:[]|{value:[]}}).
function unwrapArray(json: unknown): Record<string, unknown>[] {
  if (!json || typeof json !== 'object') return [];
  const j = json as { success?: boolean; data?: unknown };
  const d = j.data;
  if (Array.isArray(d)) return d as Record<string, unknown>[];
  if (d && typeof d === 'object') {
    const v = (d as { value?: unknown }).value;
    if (Array.isArray(v)) return v as Record<string, unknown>[];
  }
  return [];
}

// ── Helpers ─────────────────────────────────────────────────────────────────

export function isStandaloneId(id: string): boolean {
  return typeof id === 'string' && id.indexOf('st_') === 0;
}

/** Parse a release-checklist composite id "releaseId:idx" (indexOf/lastIndexOf so
 *  a releaseId containing colons still parses, mirrors legacy openDrawer). */
export function parseReleaseRef(id: string): { releaseId: string; taskIdx: number } {
  const s = String(id);
  const last = s.lastIndexOf(':');
  if (last === -1) return { releaseId: s, taskIdx: 0 };
  return { releaseId: s.slice(0, last), taskIdx: parseInt(s.slice(last + 1), 10) || 0 };
}

function str(v: unknown): string { return v == null ? '' : String(v); }

function normSubtasks(raw: unknown): TaskSubtask[] {
  const arr = Array.isArray(raw) ? raw : [];
  return arr
    .map((s) => {
      const o = (s || {}) as Record<string, unknown>;
      return {
        id: str(o.id) || 'sub_' + Math.random().toString(36).slice(2, 9),
        title: str(o.title),
        done: !!o.done,
      };
    })
    .filter((s) => s.title);
}

function normPerson(raw: unknown): TaskPerson | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  return {
    id: o.id != null ? str(o.id) : undefined,
    contactId: (o.contactId as string | number | null) ?? null,
    firstName: str(o.firstName) || undefined,
    lastName: str(o.lastName) || undefined,
    name: str(o.name) || undefined,
    initials: str(o.initials) || undefined,
    email: str(o.email) || undefined,
    avatarUrl: (o.avatarUrl as string) || (o.avatar_url as string) || (o.photo_url as string) || undefined,
    role: str(o.role) || undefined,
    source: str(o.source) || undefined,
  };
}

function normVisibility(raw: unknown): TaskVisibility {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    label: o.label !== false,
    management: o.management !== false,
    booking: o.booking !== false,
  };
}

function normApproval(raw: unknown): TaskApproval {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    status: str(o.status) || 'pending',
    approver_id: str(o.approver_id),
    decided_at: str(o.decided_at),
    revision_notes: str(o.revision_notes),
  };
}

function normComments(raw: unknown): TaskComment[] {
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map((c) => {
    const o = (c || {}) as Record<string, unknown>;
    return {
      id: str(o.id),
      ts: str(o.ts),
      actor_id: str(o.actor_id) || undefined,
      actor_name: str(o.actor_name) || undefined,
      body: str(o.body),
    };
  });
}

function normLinks(raw: unknown): TaskLink[] {
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map((l) => {
    const o = (l || {}) as Record<string, unknown>;
    return { id: str(o.id), url: str(o.url), label: str(o.label) || undefined, ts: str(o.ts) || undefined };
  });
}

function normActivity(raw: unknown): TaskActivity[] {
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map((a) => {
    const o = (a || {}) as Record<string, unknown>;
    return {
      ts: str(o.ts),
      actor_id: str(o.actor_id) || undefined,
      actor_name: str(o.actor_name) || undefined,
      action: str(o.action),
      from: o.from ?? null,
      to: o.to ?? null,
    };
  });
}

// ── Contacts (assignee picker source) ────────────────────────────────────────
async function fetchTaskContacts(artistId: string | undefined): Promise<TaskPerson[]> {
  const json = await getBlob(bust('/api/data/contacts'), artistId);
  const arr = unwrapArray(json);
  return arr
    .map((c) => normPerson(c))
    .filter((p): p is TaskPerson => !!p && !!p.id);
}

// ── Fetch a full task record by id ────────────────────────────────────────────

/**
 * Hydrate the full task record for the drawer, reading the band's blob directly.
 * @param id       standalone "st_…" or release-checklist "releaseId:idx".
 * @param artistId roster surface → the selected artist's band id (X-Band-Id);
 *                 omit on the artist's own surface / release page (ambient band).
 */
export async function fetchDrawerTask(id: string, artistId?: string): Promise<DrawerTask | null> {
  if (isStandaloneId(id)) return fetchStandaloneDrawerTask(id, artistId);
  return fetchReleaseDrawerTask(id, artistId);
}

async function fetchStandaloneDrawerTask(id: string, artistId?: string): Promise<DrawerTask | null> {
  const [blob, contacts] = await Promise.all([
    getBlob(bust('/api/data/standalone_tasks'), artistId),
    fetchTaskContacts(artistId),
  ]);
  const list = unwrapArray(blob);
  const t = list.find((x) => x && str(x.id) === id);
  if (!t) return null;
  const assignees = (Array.isArray(t.assignees) ? t.assignees : [])
    .map((a) => normPerson(a))
    .filter((p): p is TaskPerson => !!p);
  return {
    id,
    artistId: artistId || getCurrentBandId(),
    standalone: true,
    releaseId: '',
    taskIdx: -1,
    releaseTitle: '',
    releaseDate: '',
    title: str(t.title),
    due: str(t.due_date).slice(0, 10),
    status: str(t.status) || 'open',
    done: t.status === 'approved' || t.status === 'done',
    notes: str(t.description),
    assignee: assignees[0] || null,
    assignees,
    initiator: normPerson(t.initiator),
    approval: normApproval(t.approval),
    links: normLinks(t.links),
    comments: normComments(t.comments),
    activity: normActivity(t.activity),
    completed_at: str(t.completed_at),
    completed_by: normPerson(t.completed_by),
    archived_forever: !!t.archived_forever,
    visibility: normVisibility(t.visibility),
    subtasks: normSubtasks(t.subtasks),
    contacts,
  };
}

// Read a release's checklist item list, whatever field it lives under.
function checklistOf(rel: Record<string, unknown>): Record<string, unknown>[] {
  const keys = ['checklist', 'prep_checklist', 'tasks', 'todos'];
  for (const k of keys) {
    if (Array.isArray(rel[k])) return rel[k] as Record<string, unknown>[];
  }
  return [];
}

async function fetchReleaseDrawerTask(id: string, artistId?: string): Promise<DrawerTask | null> {
  const { releaseId, taskIdx } = parseReleaseRef(id);
  const [blob, contacts] = await Promise.all([
    getBlob(bust('/api/data/releases'), artistId),
    fetchTaskContacts(artistId),
  ]);
  const releases = unwrapArray(blob);
  // Match the release by id, then by index (fallback for legacy numeric refs).
  let rel = releases.find((r) => r && str(r.id) === releaseId);
  if (!rel) {
    const n = parseInt(releaseId, 10);
    if (!Number.isNaN(n) && releases[n]) rel = releases[n];
  }
  if (!rel) return null;
  const list = checklistOf(rel);
  const item = list[taskIdx];
  if (!item) return null;

  const assigneeRaw = item.assignee;
  const assignees = (Array.isArray(item.assignees) ? item.assignees : [])
    .map((a) => normPerson(a))
    .filter((p): p is TaskPerson => !!p);
  const singleAssignee = assignees[0]
    || (typeof assigneeRaw === 'string' && assigneeRaw ? { firstName: assigneeRaw } : normPerson(assigneeRaw));
  const statusRaw = str(item.status)
    || (item.done === true || item.completed === true ? 'done' : 'not_started');

  return {
    id,
    artistId: artistId || getCurrentBandId(),
    standalone: false,
    releaseId,
    taskIdx,
    releaseTitle: str(rel.name) || str(rel.title),
    releaseDate: str(rel.release_date) || str(rel.date),
    title: str(item.task) || str(item.label) || str(item.title),
    due: (str(item.date) || str(item.due) || str(item.due_date)).slice(0, 10),
    status: statusRaw,
    done: statusRaw === 'done' || statusRaw === 'approved' || item.done === true || item.completed === true,
    notes: str(item.notes) || str(item.description),
    assignee: singleAssignee,
    assignees,
    initiator: normPerson(item.initiator) || normPerson(item.from),
    approval: normApproval(item.approval),
    links: normLinks(item.links),
    comments: normComments(item.comments),
    activity: normActivity(item.activity),
    completed_at: str(item.completed_at),
    completed_by: normPerson(item.completed_by),
    archived_forever: !!item.archived_forever,
    visibility: normVisibility(item.visibility),
    subtasks: normSubtasks(item.subtasks),
    contacts,
  };
}

// ── Persist a mutated task record ─────────────────────────────────────────────
// The drawer builds the FULL next task record and hands it here; we read the
// current blob fresh, splice the record in by id, and PUT the whole array back.
// Optimistic-with-rollback lives in the drawer, this returns true only on a 2xx.

/** Serialize a DrawerTask back into the on-blob standalone_tasks record shape. */
function toStandaloneRecord(t: DrawerTask, prev: Record<string, unknown> | undefined): Record<string, unknown> {
  const now = new Date().toISOString();
  return {
    ...(prev || {}),
    id: t.id,
    title: t.title.slice(0, 500),
    description: t.notes.slice(0, 4000),
    status: t.status,
    assignees: t.assignees,
    due_date: t.due,
    visibility: t.visibility,
    initiator: t.initiator || (prev ? prev.initiator : null) || null,
    links: t.links,
    comments: t.comments.filter((c) => !c._pending && !c._failed).map(stripCommentFlags),
    activity: t.activity,
    approval: t.approval,
    subtasks: t.subtasks,
    completed_at: t.completed_at,
    completed_by: t.completed_by,
    archived_forever: t.archived_forever,
    updated_at: now,
  };
}

function stripCommentFlags(c: TaskComment): Record<string, unknown> {
  return { id: c.id, ts: c.ts, actor_id: c.actor_id || '', actor_name: c.actor_name || '', body: c.body };
}

async function saveStandalone(t: DrawerTask): Promise<boolean> {
  const artistId = t.artistId || undefined;
  const blob = await getBlob(bust('/api/data/standalone_tasks'), artistId);
  const list = unwrapArray(blob);
  const idx = list.findIndex((x) => x && str(x.id) === t.id);
  const record = toStandaloneRecord(t, idx >= 0 ? list[idx] : undefined);
  if (idx >= 0) list[idx] = record; else list.push(record);
  return putBlob('/api/data/standalone_tasks', artistId, list);
}

async function saveReleaseTask(t: DrawerTask): Promise<boolean> {
  const artistId = t.artistId || undefined;
  const blob = await getBlob(bust('/api/data/releases'), artistId);
  const releases = unwrapArray(blob);
  let relIdx = releases.findIndex((r) => r && str(r.id) === t.releaseId);
  if (relIdx < 0) {
    const n = parseInt(t.releaseId, 10);
    if (!Number.isNaN(n) && releases[n]) relIdx = n;
  }
  if (relIdx < 0) return false;
  const rel = releases[relIdx];
  // Find whichever checklist field this release uses; default to 'checklist'.
  const key = (['checklist', 'prep_checklist', 'tasks', 'todos'] as const)
    .find((k) => Array.isArray(rel[k])) || 'checklist';
  const list = Array.isArray(rel[key]) ? (rel[key] as Record<string, unknown>[]).slice() : [];
  const item = list[t.taskIdx];
  if (!item) return false;
  list[t.taskIdx] = {
    ...item,
    task: t.title,
    label: item.label != null ? t.title : item.label,
    notes: t.notes,
    status: t.status,
    done: t.status === 'done' || t.status === 'approved',
    due: t.due,
    date: item.date != null ? t.due : item.date,
    assignee: t.assignee,
    assignees: t.assignees,
    initiator: t.initiator,
    approval: t.approval,
    links: t.links,
    comments: t.comments.filter((c) => !c._pending && !c._failed).map(stripCommentFlags),
    activity: t.activity,
    subtasks: t.subtasks,
    visibility: t.visibility,
    completed_at: t.completed_at,
    completed_by: t.completed_by,
    archived_forever: t.archived_forever,
    updated_at: new Date().toISOString(),
  };
  releases[relIdx] = { ...rel, [key]: list };
  return putBlob('/api/data/releases', artistId, releases);
}

/** Persist the full task record back to its band blob. Returns true on a 2xx. */
export async function saveDrawerTask(t: DrawerTask): Promise<boolean> {
  return t.standalone ? saveStandalone(t) : saveReleaseTask(t);
}

/** Delete the whole task from its band blob. Returns true on a 2xx. */
export async function deleteDrawerTask(t: DrawerTask): Promise<boolean> {
  const artistId = t.artistId || undefined;
  if (t.standalone) {
    const blob = await getBlob(bust('/api/data/standalone_tasks'), artistId);
    const list = unwrapArray(blob).filter((x) => !(x && str(x.id) === t.id));
    return putBlob('/api/data/standalone_tasks', artistId, list);
  }
  const blob = await getBlob(bust('/api/data/releases'), artistId);
  const releases = unwrapArray(blob);
  let relIdx = releases.findIndex((r) => r && str(r.id) === t.releaseId);
  if (relIdx < 0) {
    const n = parseInt(t.releaseId, 10);
    if (!Number.isNaN(n) && releases[n]) relIdx = n;
  }
  if (relIdx < 0) return false;
  const rel = releases[relIdx];
  const key = (['checklist', 'prep_checklist', 'tasks', 'todos'] as const)
    .find((k) => Array.isArray(rel[k])) || 'checklist';
  const list = Array.isArray(rel[key]) ? (rel[key] as Record<string, unknown>[]).slice() : [];
  list.splice(t.taskIdx, 1);
  releases[relIdx] = { ...rel, [key]: list };
  return putBlob('/api/data/releases', artistId, releases);
}
